// Generated from scripts/product-inventory.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';
function isProductSelect(event) {
    return 'promise' in event && event.promise instanceof Promise;
}
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
/**
 * The stock status of the selected variant. Liquid decides what is shown, and decides it honestly: a count only
 * for a variant Shopify tracks, that does not allow overselling, with stock above zero and at or below the
 * merchant's threshold. This element follows the variant picker of its section (or dialog): when Shopify has
 * rendered the section for a new selection it morphs its status line from the block it finds there, so the
 * server's markup stays the one definition, and it moves `data-status`, which colors the dot.
 *
 * The status line is not a live region on first paint. A separate, visually hidden polite region is empty until a
 * selection changes what the line says, and then it says the new line once. A selection without a variant (an
 * unavailable combination) arrives with no resource and no block that describes it, so it shows the translated
 * "unavailable" text from `data-unavailable-label` as sold out.
 *
 * Contract kept from the inherited module: the `product-inventory` element, `data-product-id`, and following the
 * standard product select event of its section; product cards are skipped.
 */
class ProductInventory extends Component {
    #lifetime;
    /** Counts the selections seen, so only the newest one decides what is shown. */
    #selection = 0;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const section = this.closest('.shopify-section, dialog');
        if (!section)
            return;
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal: lifetime.signal });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
    }
    /** The line the shopper reads now. */
    #statusText() {
        return this.querySelector('[data-inventory-status]')?.textContent?.trim() ?? '';
    }
    /** Says the new line in the polite region, once. */
    #announce(text) {
        const region = this.querySelector('[data-inventory-live]');
        if (region && text)
            region.textContent = text;
    }
    #handleProductSelect = (event) => {
        if (!isProductSelect(event) || !(event.target instanceof Element) || event.target.closest('product-card'))
            return;
        const selection = ++this.#selection;
        event.promise
            .then(({ detail }) => {
            if (selection !== this.#selection || !detail)
                return;
            const { html, newProduct, resource } = detail;
            if (newProduct) {
                this.dataset.productId = newProduct.id;
            }
            else if (detail.productId && detail.productId !== this.dataset.productId) {
                return;
            }
            const before = this.#statusText();
            if (resource === null) {
                const text = this.querySelector('[data-inventory-text]');
                if (!text || !this.dataset.unavailableLabel)
                    return;
                text.textContent = this.dataset.unavailableLabel;
                this.dataset.status = 'sold_out';
            }
            else {
                // The section answers with every inventory block of the page; this one has its block id.
                const incoming = Array.from(html.querySelectorAll('product-inventory')).find((candidate) => candidate instanceof HTMLElement && candidate.dataset.blockId === this.dataset.blockId);
                const next = incoming?.querySelector('[data-inventory-status]');
                const current = this.querySelector('[data-inventory-status]');
                if (!(incoming instanceof HTMLElement) || !next || !current)
                    return;
                morph(current, next);
                this.dataset.status = incoming.dataset.status ?? '';
            }
            const after = this.#statusText();
            if (after !== before)
                this.#announce(after);
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[product-inventory] Event promise rejected:', error);
        });
    };
}
if (!customElements.get('product-inventory')) {
    customElements.define('product-inventory', ProductInventory);
}
